import _classnames from 'clsx';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import Select, { optionproptypes } from '../select/select';

/**
 * This functional component combines a text input element with a `Select` component, allowing the user to
 * specify a unit or other related data in addition to the text input value.
 */
function TextSelect({
  classes,
  disabled,
  innerRef,
  selectClasses,
  selectOnChange,
  selectOptGroups,
  selectOptions,
  selectPlaceholder,
  selectRef,
  selectValue,
  status,
  textId,
  textName,
  textOnChange,
  textPlaceholder,
  textValue,
  textInputClasses,
  type,
}) {
  return (
    <div
      className={_classnames('TextSelect', classes, {
        'TextSelect--disabled': disabled,
        [`TextSelect--${status}`]: status,
      })}
    >
      <div className={_classnames('TextSelect--text', textInputClasses)}>
        <input
          className={_classnames('TextInput TextInput--full-width TextInput--textselect', {
            [`TextInput--${status}`]: status,
          })}
          disabled={disabled}
          id={textId}
          ref={innerRef}
          type={type ?? 'text'}
          name={textName}
          onChange={textOnChange}
          placeholder={textPlaceholder}
          value={textValue}
        />
      </div>
      <div className={_classnames('TextSelect--select', selectClasses)}>
        <Select
          classes="Select--textselect"
          disabled={disabled}
          innerRef={selectRef}
          onChange={selectOnChange}
          optGroups={selectOptGroups}
          options={selectOptions}
          placeholder={selectPlaceholder}
          status={status}
          value={selectValue}
        />
      </div>
    </div>
  );
}

TextSelect.propTypes = {
  /**
   * A space-separated list of classes passed to the outer `div` wrapping the component
   */
  classes: PropTypes.string,
  /**
   * Disabled state grays out both the text input and the select input and does not allow any
   * input changes.
   */
  disabled: PropTypes.bool,
  /**
   * A space-separated list of classes to apply to the `div` wrapping the Select component
   */
  selectClasses: PropTypes.string,
  /**
   * The `onChange` function passed to the `Select` component
   */
  selectOnChange: PropTypes.func.isRequired,
  /**
   * If the options within the select element should be grouped, this prop should be used rather
   * than the `selectOptions` prop. The objects in the array for this prop should include a `label` key for
   * each group and an `options` array in the same expected format as the `selectOptions` prop.
   */
  selectOptGroups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: optionproptypes,
    })
  ),
  /**
   * The options available inside the select element; can be either an array of strings or an array
   * of objects
   */
  selectOptions: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        displayValue: PropTypes.string.isRequired,
      })
    ),
  ]),
  /**
   * Placeholder value shown in `Select` component before an option has been selected
   */
  selectPlaceholder: PropTypes.string,
  /**
   * The value passed to the `Select` component
   */
  selectValue: PropTypes.string.isRequired,
  /**
   * Adds a class to the `div` wrapping the component as well as both input elements
   * (`.Select--yourStatusStringHere`).
   * Current styles exist for these statuses: `success`, `warning`
   */
  status: PropTypes.string,
  /**
   * The value of the `id` prop on the text input element
   */
  textId: PropTypes.string.isRequired,
  /**
   * The value of the `name` prop on the text input element
   */
  textName: PropTypes.string.isRequired,
  /**
   * A space-separated list of classes applied to the `div` wrapping the text input element
   */
  textInputClasses: PropTypes.string,
  /**
   * The `onChange` function passed to the text input element
   */
  textOnChange: PropTypes.func.isRequired,
  /**
   * The placeholder text to be shown in the input before the input value is changed
   */
  textPlaceholder: PropTypes.string,
  /**
   * The value passed to the text input element
   */
  textValue: PropTypes.string.isRequired,
  /**
   * If left `null` or `undefined`, the input element is a text input. Include this prop to
   * another input type to change the main input type.
   */
  type: PropTypes.string,
  /**
   * Inner Ref
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  /**
   * Select ref
   */
  selectRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

TextSelect.defaultProps = {
  classes: undefined,
  innerRef: undefined,
  disabled: undefined,
  selectClasses: undefined,
  selectOptGroups: undefined,
  selectOptions: undefined,
  selectPlaceholder: undefined,
  status: undefined,
  textInputClasses: undefined,
  textPlaceholder: undefined,
  type: undefined,
  selectRef: undefined,
};

export default forwardRef((props, ref) => <TextSelect innerRef={ref} {...props} />);
